import React from 'react'
import Link from 'next/link'
import { Button } from './ui/button'
import { motion } from 'framer-motion'

const HelpMegaMenu = ({ item }) => {
  const { documentation, guides, apiReference, liveHelp, community } = item.data

  return (
    <>
      <div className="grid grid-cols-2 gap-8 p-5">
        <div>
          {/* Documentation Section */}
          <motion.div
            className="mb-8 rounded-lg bg-purple-50 p-6"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <h3 className="mb-2 text-xl font-semibold">{documentation.title}</h3>
            <p className="mb-4 text-gray-600">{documentation.description}</p>
            <Button className="w-full" asChild>
              <Link href={documentation.buttonLink}>{documentation.buttonText}</Link>
            </Button>
          </motion.div>

          {/* Guides Section */}
          <motion.div
            className="mb-6"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <h3 className="mb-3 text-lg font-semibold">{guides.title}</h3>
            <p className="mb-4 text-sm text-gray-600">{guides.description}</p>
            <div className="space-y-3">
              {guides.links.map((link, index) => {
                const Icon = link.icon
                return (
                  <motion.div
                    key={`guide-link-${index}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + index * 0.05, duration: 0.2 }}
                  >
                    <Link href={link.href} className="flex items-center gap-2 text-blue-600 hover:underline">
                      <Icon size={18} strokeWidth={2} />
                      {link.text}
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>

          {/* API Reference Section */}
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.2 }}
          >
            <h3 className="mb-3 text-lg font-semibold">{apiReference.title}</h3>
            <p className="mb-4 text-sm text-gray-600">{apiReference.description}</p>
            <div className="space-y-3">
              {apiReference.links.map((link, index) => {
                const Icon = link.icon
                return (
                  <motion.div
                    key={`api-link-${index}`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.05, duration: 0.2 }}
                  >
                    <Link href={link.href} className="flex items-center gap-2 text-blue-600 hover:underline">
                      <Icon size={18} strokeWidth={2} />
                      {link.text}
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>

        <div>
          {/* Live Help Section */}
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            <h3 className="mb-3 text-lg font-semibold">{liveHelp.title}</h3>
            <p className="mb-4 text-sm text-gray-600">{liveHelp.description}</p>

            <div className="grid grid-cols-2 gap-3">
              {liveHelp.categories.map((category, index) => {
                const Icon = category.icon
                return (
                  <motion.div
                    key={`category-${index}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + index * 0.05, duration: 0.2 }}
                  >
                    <Link
                      href={category.href}
                      className="flex flex-col items-center rounded-lg border p-3 transition-colors hover:bg-gray-50"
                    >
                      <Icon className={`mb-2 ${category.iconColor}`} size={24} strokeWidth={2} />
                      <span className="text-sm font-medium">{category.text}</span>
                    </Link>
                  </motion.div>
                )
              })}

              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.2 }}
                className="col-span-2"
              >
                <Link
                  href={liveHelp.supportButton.href}
                  className="flex items-center justify-center gap-2 rounded-lg bg-black p-3 text-white transition-colors hover:bg-gray-800"
                >
                  {React.createElement(liveHelp.supportButton.icon, { size: 20, strokeWidth: 2 })}
                  <span className="font-medium">{liveHelp.supportButton.text}</span>
                </Link>
              </motion.div>
            </div>
          </motion.div>

          {/* Community Section */}
          <motion.div
            className="mt-6"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: 0.3 }}
          >
            <h3 className="mb-3 text-lg font-semibold">{community.title}</h3>
            <div className="flex flex-wrap gap-3">
              {community.links.map((link, index) => {
                const Icon = link.icon
                return (
                  <motion.div
                    key={`community-link-${index}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.05, duration: 0.2 }}
                  >
                    <Link
                      href={link.href}
                      className="flex items-center gap-2 rounded-md bg-gray-100 px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-200"
                    >
                      <Icon size={18} strokeWidth={2} />
                      {link.text}
                    </Link>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  )
}

export default HelpMegaMenu
